import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Cookie from 'utils/cookies';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import InViewObserver from 'components/InViewObserver/InViewObserver';
import { UNIFIED_FEED_DOMAIN_SERVICE } from 'constants';
import makeRequest from 'utils/makeRequest';
import LoaderSpinner from 'components_v2/common/LoaderSpinner';
import {
  checkIframeUserLogin,
  initializeIframeMessageListener,
} from 'routes/games/layouts/utils';
import { loadSyncUp } from '../../../redux';
import GameOverlay from '../GameOverlay/GameOverlay';
import s from './PreviousPuzzleCard.scss';

const PreviousPuzzleCard = props => {
  const {
    isUserLoggedIn,
    userData,
    previousPuzzleData,
    gameId,
    gameType,
    gameLevel,
    isGameIframe,
    iframeSource,
    iframeLang,
    isMobile,
    openLoginBox = () => {},
  } = props;

  const [hasSynced, setHasSynced] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showLoader, setLoader] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calendarCache, setCalendarCache] = useState({});
  const userDataRef = useRef(userData);

  let grxId = Cookie.get('_grx');
  let ssoId = Cookie.get('ssoid');
  grxId = grxId || '';
  ssoId = ssoId || '';

  const gameSyncCall = () => {
    let user = {};
    if (userDataRef && userDataRef.current && userDataRef.current.isLoggedIn) {
      user = userDataRef.current;
    }
    const params = {
      userName:
        user && typeof user.getFirstName === 'function'
          ? user?.getFirstName()
          : 'N/A',
      userImage:
        user && typeof user.getIcon === 'function' ? user?.getIcon() : '',
      grxId,
      ssoId,
    };
    props.loadSyncUp(params);
  };

  const getCurrentLevel = () => {
    const levels = previousPuzzleData?.difficultyLevelData?.levels || [];
    const playedLevels = levels.filter(level => level.hasPlayed === true);

    if (playedLevels.length === 0) {
      return 'easy'; // Default if no levels played
    } else if (playedLevels.length === 1) {
      return playedLevels[0].label?.toLowerCase() || 'easy';
    }
    // If multiple levels played, default to easy
    return 'easy';
  };

  const getCalendarData = (month = null, year = null, level = null) => {
    const tempPreviousPuzzleData = {
      gameIcon: previousPuzzleData?.practicePuzzleData?.gameIcon || '',
      gameName:
        previousPuzzleData?.practicePuzzleData?.gameName || 'Classic Sudoku',
    };
    if (!isUserLoggedIn) {
      setCalendarData(tempPreviousPuzzleData);
      return;
    }
    // Use provided month/year or extract from previousPuzzleData
    let targetMonth = month;
    let targetYear = year;

    if (month === null || year === null) {
      // Extract month and year from the last item in practicePuzzleData.items
      const puzzleItems = previousPuzzleData?.practicePuzzleData?.items || [];

      if (puzzleItems.length > 0) {
        const lastItem = puzzleItems[puzzleItems.length - 1];
        const lastItemDate = new Date(lastItem.gameDate);
        targetMonth = String(lastItemDate.getMonth() + 1).padStart(2, '0');
        targetYear = lastItemDate.getFullYear();
      } else {
        targetMonth = String(currentMonth).padStart(2, '0');
        targetYear = currentYear;
      }
    }

    const currentLevel = level || getCurrentLevel();

    // Create cache key
    const cacheKey = `${targetMonth}-${targetYear}-${currentLevel}`;

    // Check if data is already cached
    if (calendarCache[cacheKey]) {
      setCalendarData(calendarCache[cacheKey]);
      setCurrentMonth(parseInt(targetMonth, 10));
      setCurrentYear(targetYear);
      return;
    }
    const formattedGameType = gameType.replace(/-/g, '_');
    const excludeLevelGames = [
      'crossword',
      'location_guesser',
      'mini_crossword',
    ];
    const baseUrl = `${UNIFIED_FEED_DOMAIN_SERVICE}/ufs-utility/toi-games/calendar/widget?pc=toi&pfm=web&fv=1250&gameId=${gameId}&gameType=${formattedGameType}&month=${targetMonth}&year=${targetYear}`;
    const url = excludeLevelGames.includes(formattedGameType)
      ? baseUrl
      : `${baseUrl}&level=${currentLevel}`;
    const requestParams = {
      params: {},
      headers: {
        grxId,
        ssoId,
      },
    };
    setLoader(true);
    makeRequest
      .get(url, requestParams, 'skipfeedengine')
      .then(data => {
        if (data && data.data && Array.isArray(data.data.items)) {
          const finalPreviousPuzzleData = {
            ...data.data,
            ...tempPreviousPuzzleData,
            currentLevel,
            currentMonth: parseInt(targetMonth, 10),
            currentYear: targetYear,
            monthYearDisplay: (() => {
              const monthNames = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
              ];
              const monthName =
                monthNames[parseInt(targetMonth, 10) - 1] || 'January';
              return `${monthName} ${targetYear}`;
            })(),
          };

          // Cache the data
          setCalendarCache(prevCache => ({
            ...prevCache,
            [cacheKey]: finalPreviousPuzzleData,
          }));

          setCalendarData(finalPreviousPuzzleData);

          // Update current month/year state
          setCurrentMonth(parseInt(targetMonth, 10));
          setCurrentYear(targetYear);
        }
        setLoader(false);
      })
      .catch(err => {
        console.log('Calendar data error:', err);
        setLoader(false);
      });
  };

  const handleCalendarNavigation = direction => {
    let newMonth = currentMonth;
    let newYear = currentYear;

    if (direction === 'previous') {
      if (newMonth === 1) {
        newMonth = 12;
        newYear -= 1;
      } else {
        newMonth -= 1;
      }
    } else if (direction === 'next') {
      if (newMonth === 12) {
        newMonth = 1;
        newYear += 1;
      } else {
        newMonth += 1;
      }
    }

    getCalendarData(newMonth, newYear);
  };

  const handleLevelChange = newLevel => {
    // Call getCalendarData with current month/year but new level
    getCalendarData(currentMonth, currentYear, newLevel);
  };

  useEffect(
    () => {
      if (typeof props.loadSyncUp === 'function') {
        userDataRef.current = userData;
        if (userData && userData.isLoggedIn && !hasSynced) {
          gameSyncCall();
          setHasSynced(true);
        }
        if (userData && !userData.isLoggedIn) {
          setHasSynced(false);
        }
      }
    },
    [userData, hasSynced, props.loadSyncUp],
  );

  useEffect(
    () => {
      if (isGameIframe && iframeSource) {
        const cleanup = initializeIframeMessageListener({
          onLoginStatus: data => {
            if (data && data.userData && data.userData.userDetails) {
              // Update grxId and ssoId with iframe data if available
              const tempGrxId = data?.userData?.grxId || grxId;
              const tempSsoId = data?.userData?.userDetails?.ssoid || ssoId;
              userDataRef.current = {
                ...userDataRef.current,
                isLoggedIn: true,
                getFirstName:
                  data.userData.userDetails.FL_N ||
                  data.userData.userDetails.loginId ||
                  '',
                getIcon: data.userData.userDetails.profileImageUrl || '',
              };
              if (data?.userData?.userDetails?.ssoid) {
                const params = {
                  userName:
                    data.userData.userDetails.FL_N ||
                    data.userData.userDetails.loginId ||
                    'N/A',
                  userImage: data.userData.userDetails.profileImageUrl || '',
                  grxId: tempGrxId,
                  ssoId: tempSsoId,
                };
                props.loadSyncUp(params);
              }
              setHasSynced(true);
            } else {
              console.log(`Could not login`);
            }
            cleanup(); // Clean up listener after receiving data
          },
        });
        // Check login status
        checkIframeUserLogin(iframeSource, iframeLang);
        return cleanup;
      }
      return undefined;
    },
    [isGameIframe, iframeSource, iframeLang],
  );

  const handleOverlayClose = () => {
    setShowOverlay(false);
  };

  const renderLevelSelector = () => {
    const levels = previousPuzzleData?.difficultyLevelData?.levels || [];

    return (
      <div className={s.gameLevelContainer}>
        <h2>
          {previousPuzzleData?.difficultyLevelData?.title ||
            'Play other levels'}
        </h2>
        <p>
          {previousPuzzleData?.difficultyLevelData?.description ||
            'You&apos;ve played Easy mode. Want to explore the other challenges?'}
        </p>
        <div className={s.levelButtons}>
          {levels.map((level, index) => {
            const isActive = level.hasPlayed === true;
            const levelClass = level.label?.toLowerCase() || '';

            return (
              <button
                key={index}
                className={`${s.levelBtn} ${s[levelClass]} ${
                  isActive ? s.active : ''
                }`}
                onClick={() => {
                  const gaData = {
                    event: 'user_click_games',
                    type: 'level_widget',
                    label: level.label || 'Level',
                    page_template: 'gameplay',
                    section: 'games',
                    subsection:
                      gameType === 'loop-a-loop'
                        ? 'loop_the_loop'
                        : gameType?.replace(/-/g, '_'),
                    browsing_platform: isMobile ? 'mweb' : 'web',
                    game_id: gameId,
                    level: gameLevel || 'N/A',
                    game_of_the_day: window.location.href
                      .toLowerCase()
                      .includes('/today'),
                    login_status: !!ssoId,
                    user_grx_id: grxId,
                  };
                  if (ssoId) {
                    gaData.sso_id = ssoId;
                  }
                  analyticsWrapper('ga4', gaData);
                  analyticsWrapper('grx', 'track', 'user_click_games', gaData);
                  // Redirect to the game page
                  window.location.href = `/games/${gameType}/today`;
                }}
              >
                <InViewObserver
                  inViewCallback={() => {
                    const gaData = {
                      event: 'user_shown_games',
                      type: 'level_widget',
                      label: level.label || 'Level',
                      page_template: 'gameplay',
                      section: 'games',
                      subsection:
                        gameType === 'loop-a-loop'
                          ? 'loop_the_loop'
                          : gameType?.replace(/-/g, '_'),
                      browsing_platform: isMobile ? 'mweb' : 'web',
                      game_id: gameId,
                      level: gameLevel || 'N/A',
                      game_of_the_day: window.location.href
                        .toLowerCase()
                        .includes('/today'),
                      login_status: !!ssoId,
                      user_grx_id: grxId,
                    };
                    if (ssoId) {
                      gaData.sso_id = ssoId;
                    }
                    analyticsWrapper('ga4', gaData);
                    analyticsWrapper(
                      'grx',
                      'track',
                      'user_shown_games',
                      gaData,
                    );
                  }}
                >
                  <strong>{level.label || 'Level'}</strong>
                </InViewObserver>{' '}
                {isActive && <i className={`${s.gamePlayIcon} ${s.played}`} />}
                {!isActive && <i className={`${s.gamePlayIcon}`} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPreviousPuzzles = () => {
    // Use API data if available, otherwise fallback to generated dates
    const puzzleItems = previousPuzzleData?.practicePuzzleData?.items || [];

    let weekDates;
    if (puzzleItems.length > 0) {
      // Use API data - limit items based on platform
      const maxItems = typeof isMobile === 'boolean' && isMobile ? 3 : 7;
      const limitedItems = puzzleItems.slice(0, maxItems);
      weekDates = limitedItems.map(item => ({
        date: item.date || '',
        day: item.day || '',
        month: item.date.split(' ')[1] || '',
        isCompleted:
          item.cta &&
          Array.isArray(item.cta) &&
          item.cta.length > 0 &&
          item.cta.every(ctaItem => ctaItem.hasPlayed === true),
        cta: item.cta || [],
      }));
    } else {
      // Fallback to current logic - limit items based on platform
      const maxItems = typeof isMobile === 'boolean' && isMobile ? 3 : 7;
      const today = new Date();
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      weekDates = Array.from({ length: maxItems }, (_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() - today.getDay() + i);
        return {
          cta: [],
          date: date.getDate(),
          day: days[date.getDay()],
          month: date.toLocaleString('default', { month: 'short' }),
          isCompleted: Math.random() > 0.5, // This should be replaced with actual completion data
        };
      });
    }

    return (
      <>
        <div className={s.previousPuzzlesContainer}>
          {previousPuzzleData &&
            previousPuzzleData.difficultyLevelData &&
            Array.isArray(previousPuzzleData.difficultyLevelData.levels) &&
            previousPuzzleData.difficultyLevelData.levels.length > 0 &&
            !previousPuzzleData.difficultyLevelData.levels.every(
              level => level.hasPlayed === true,
            ) &&
            renderLevelSelector()}
          <h2>
            {previousPuzzleData && previousPuzzleData.practicePuzzleData
              ? previousPuzzleData.practicePuzzleData.title
              : 'Play Previous Puzzles'}
          </h2>

          <InViewObserver
            inViewCallback={() => {
              const gaData = {
                event: 'user_shown_games',
                type: 'game_practice_widget',
                label: 'congratulations',
                category: 'congratulations',
                page_template: 'gameplay',
                section: 'games',
                subsection:
                  gameType === 'loop-a-loop'
                    ? 'loop_the_loop'
                    : gameType?.replace(/-/g, '_'),
                browsing_platform: isMobile ? 'mweb' : 'web',
                game_id: gameId,
                level: gameLevel || 'N/A',
                game_of_the_day: window.location.href
                  .toLowerCase()
                  .includes('/today'),
                login_status: !!ssoId,
                user_grx_id: grxId,
              };
              if (ssoId) {
                gaData.sso_id = ssoId;
              }
              analyticsWrapper('ga4', gaData);
              analyticsWrapper('grx', 'track', 'user_shown_games', gaData);
            }}
          >
            <div
              className={`${s.puzzlesContainer} ${
                gameType ? `${gameType}` : ''
              }`}
            >
              {weekDates.map((day, index) => (
                <div
                  key={index}
                  className={`${s.puzzleCard} ${
                    isUserLoggedIn &&
                    day.cta &&
                    day.cta[0] &&
                    'hasPlayed' in day.cta[0] &&
                    day.cta[0].id
                      ? s.clickable
                      : ''
                  } ${gameType ? `${gameType}` : ''}`}
                  // style={{
                  //   cursor:
                  //     isUserLoggedIn &&
                  //     day.cta &&
                  //     day.cta[0] &&
                  //     'hasPlayed' in day.cta[0] &&
                  //     day.cta[0].id
                  //       ? 'pointer'
                  //       : 'default',
                  // }}
                  onClick={() => {
                    const gaData = {
                      event: 'user_click_games',
                      type: 'game_practice_widget',
                      category: 'congratulations',
                      label:
                        puzzleItems?.length > 0
                          ? day?.date
                          : `${day?.date}th ${day?.month}`,
                      page_template: 'gameplay',
                      section: 'games',
                      subsection:
                        gameType === 'loop-a-loop'
                          ? 'loop_the_loop'
                          : gameType?.replace(/-/g, '_'),
                      browsing_platform: isMobile ? 'mweb' : 'web',
                      game_id: gameId,
                      level: gameLevel || 'N/A',
                      game_of_the_day: window?.location?.href
                        .toLowerCase()
                        .includes('/today'),
                      login_status: !!ssoId,
                      user_grx_id: grxId,
                    };
                    if (ssoId) {
                      gaData.sso_id = ssoId;
                    }
                    analyticsWrapper('ga4', gaData);
                    analyticsWrapper(
                      'grx',
                      'track',
                      'user_click_games',
                      gaData,
                    );
                    if (
                      isUserLoggedIn &&
                      day.cta &&
                      day.cta[0] &&
                      'hasPlayed' in day.cta[0] &&
                      day.cta[0].id
                    ) {
                      window.location.href = `/games/${gameType}/${
                        day.cta[0].id
                      }`;
                    }
                    if (!isUserLoggedIn) {
                      openLoginBox();
                    }
                  }}
                >
                  <div className={s.puzzleCardIconWrap}>
                    <div
                      className={`${s.puzzleCardIcon} ${
                        gameType ? `${gameType}` : ''
                      } ${day.isCompleted ? s.completed : ''} ${
                        !isUserLoggedIn ? s.locked : ''
                      }`}
                    />
                  </div>
                  <strong className={s.day}>{day.day}</strong>
                  <span className={s.date}>
                    {puzzleItems.length > 0
                      ? day.date
                      : `${day.date}th ${day.month}`}
                  </span>
                </div>
              ))}

              <div
                className={s.playMoreBtn}
                onClick={() => {
                  setShowOverlay(true);
                  getCalendarData(); // Fetch calendar data when overlay opens
                  const gaData = {
                    event: 'user_click_games',
                    type: 'game_practice_widget',
                    category: 'game_practice_popup',
                    label: 'play_more',
                    page_template: 'gameplay',
                    section: 'games',
                    subsection:
                      gameType === 'loop-a-loop'
                        ? 'loop_the_loop'
                        : gameType?.replace(/-/g, '_'),
                    browsing_platform: isMobile ? 'mweb' : 'web',
                    game_id: gameId,
                    level: gameLevel || 'N/A',
                    game_of_the_day: window.location.href
                      .toLowerCase()
                      .includes('/today'),
                    login_status: !!ssoId,
                    user_grx_id: grxId,
                  };
                  if (ssoId) {
                    gaData.sso_id = ssoId;
                  }
                  analyticsWrapper('ga4', gaData);
                  analyticsWrapper('grx', 'track', 'user_click_games', gaData);
                }}
              >
                <i className={s.calendarIcon} />
                <strong>Play More</strong>
              </div>
            </div>
            <div className={s.note}>
              Note: Streaks are only earned by playing today&apos;s game*
            </div>
          </InViewObserver>

          {isUserLoggedIn &&
            showOverlay && (
              <GameOverlay
                userData={userData}
                popUpType="calendar"
                gameId={gameId}
                gameType={gameType}
                gameOverlayCallback={handleOverlayClose}
                gameLevel={gameLevel}
                calendarData={calendarData}
                showLoader={showLoader}
                onCalendarNavigation={handleCalendarNavigation}
                onLevelChange={handleLevelChange}
              />
            )}
          {!isUserLoggedIn &&
            showOverlay && (
              <GameOverlay
                userData={userData}
                popUpType="non-logged-in-calendar"
                gameId={gameId}
                gameType={gameType}
                gameOverlayCallback={handleOverlayClose}
                gameLevel={gameLevel}
                calendarData={calendarData}
                openLoginBox={openLoginBox}
              />
            )}
        </div>
      </>
    );
  };

  return (
    <div className={s.previousPuzzlesContainer}>
      {previousPuzzleData && renderPreviousPuzzles()}
      {showLoader && (
        <div className="bgNone">
          <LoaderSpinner />
        </div>
      )}
    </div>
  );
};

PreviousPuzzleCard.propTypes = {
  userData: PropTypes.object,
  gameId: PropTypes.string,
  gameType: PropTypes.string,
  gameLevel: PropTypes.string,
  isGameIframe: PropTypes.bool,
  iframeSource: PropTypes.string,
  iframeLang: PropTypes.string,
  loadSyncUp: PropTypes.func,
  isMobile: PropTypes.bool,
  openLoginBox: PropTypes.func,
};

PreviousPuzzleCard.defaultProps = {
  userData: {},
  gameId: '',
  gameType: '',
  gameLevel: '',
  isGameIframe: false,
  iframeSource: '',
  iframeLang: '',
  loadSyncUp: () => {},
  isMobile: false,
  openLoginBox: () => {},
};

export default connect(
  state => ({
    userData: state.userData,
    gamesconfigData: state.gamesconfigData,
  }),
  {
    loadSyncUp,
  },
)(PreviousPuzzleCard);
